import { Field, Float, ID, InputType, ObjectType, registerEnumType } from 'type-graphql'
import { modelOptions, Prop } from '@typegoose/typegoose'
import { BaseEntity } from '../../base/BaseEntity'
import { generateEntityId } from '../../generateEntityId'
import { AiCreditTransactionTypeEnum } from './AiCreditTransactionTypeEnum'

// Registered beside the fields that use them so the enum files stay decorator-free for the mobile apps.
registerEnumType(AiCreditTransactionTypeEnum, {
  name: 'AiCreditTransactionTypeEnum',
  description:
    'Ledger entry type for an AiCreditTransaction — Debit consumes balance, Grant adds it (admin, free), Purchase adds it (bought by the owner), Adjustment is a manual correction.',
})

/**
 * A single ledger entry against a `CreditAccount` — a Debit (AI usage), Grant (admin
 * top-up), Adjustment (manual correction), or Purchase (self-serve top-up). `credits` is
 * signed; `balanceAfter` snapshots the account balance immediately after this entry was
 * applied.
 *
 * A Debit row additionally carries the FULL charged snapshot of the metered AI call it
 * paid for — tokens, model, and the currency/FX/markup snapshot taken at record time
 * (`unitsPerUsd`, `markupPct`, `rawCcy`, `billedCcy`) — merged directly onto this entry
 * rather than a separate linked document, so one write persists both the usage detail and
 * the debit. Grant/Adjustment/Purchase rows never touch these usage-detail fields, which
 * stay undefined for them. See `backend-entity-style-guide.md` §5.1 — a later `CurrencyRate`/
 * `ModelRate` change must never rewrite the price of a call already charged.
 *
 * Two free-text fields, deliberately split: `remarks` (inherited from `BaseEntity`) carries the
 * description of a Debit — written by the AI call site; `reason` carries the admin's free text
 * for a Grant/Adjustment entry.
 */
@ObjectType()
@InputType('AiCreditTransactionInput')
@modelOptions({ schemaOptions: { timestamps: true, collection: 'aicredittransactions' } })
export class AiCreditTransaction extends BaseEntity {
  // `aict` (not the pre-rename `crtx`) — the id prefix is deliberately renamed alongside the
  // class so it reads as "AI Credit Transaction" like the class/collection now do. Safe to change
  // silently in most cases, but called out per house convention because a prefix is normally a
  // stable identity signal; this one is free to move because the feature is still unshipped (no
  // migration needed — db-migration.md §0).
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('aict') })
  declare _id: string

  /** Generic owner of this entry — a `User._id` (User billing scope) or a `Tenant._id` (Tenant billing scope). */
  @Field(() => String)
  @Prop({ type: String, required: true })
  ownerId: string

  @Field(() => String)
  @Prop({ type: String, required: true })
  currency: string

  @Field(() => AiCreditTransactionTypeEnum)
  @Prop({ type: String, enum: AiCreditTransactionTypeEnum, required: true })
  type: AiCreditTransactionTypeEnum

  /** Signed delta applied to the balance (negative for Debit, positive for Grant). */
  @Field(() => Float)
  @Prop({ type: Number, required: true })
  credits: number

  @Field(() => Float)
  @Prop({ type: Number, required: true })
  balanceAfter: number

  /** Feature tag that caused a Debit (e.g. `quickscore.processScan`). Absent for Grant/Adjustment. */
  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  feature?: string

  /** Free-text reason, mainly for Grant/Adjustment entries. */
  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  reason?: string

  /**
   * `_id` of the acting user: the admin who granted/adjusted this entry, or the teacher whose
   * scan produced a Debit — recorded at debit time so a pooled school balance's ledger still
   * names which member drew it down.
   */
  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  createdBy?: string

  /**
   * The `CreditPurchase._id` this Purchase entry was granted for — ledger-level idempotency
   * for the credit-grant chokepoint (a unique partial index on `(tenant._id, creditPurchaseId)`
   * for `type = Purchase` rows rejects a second grant for the same purchase). Absent for
   * Debit/Grant/Adjustment entries.
   */
  @Field(() => ID, { nullable: true })
  @Prop({ type: String })
  creditPurchaseId?: string

  // --- Usage-detail snapshot, persisted only on a Debit row (RecordAndDebitAiUsage → DebitCredits).
  // A Grant/Adjustment/Purchase row never sets these — they stay undefined for it rather than 0,
  // since 0 would misread as "a free call". ---

  /** The model actually used to serve the call. */
  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  model?: string

  @Field(() => Float, { nullable: true })
  @Prop({ type: Number })
  inputTokens?: number

  @Field(() => Float, { nullable: true })
  @Prop({ type: Number })
  outputTokens?: number

  /** Raw USD cost from the model's canonical `ModelRate`, before FX/markup. */
  @Field(() => Float, { nullable: true })
  @Prop({ type: Number })
  rawUsd?: number

  /** Charged snapshot of the account's FX rate at record time. */
  @Field(() => Float, { nullable: true })
  @Prop({ type: Number })
  unitsPerUsd?: number

  /** Charged snapshot of the markup percentage at record time. */
  @Field(() => Float, { nullable: true })
  @Prop({ type: Number })
  markupPct?: number

  /** `rawUsd` converted to the account currency (before markup). */
  @Field(() => Float, { nullable: true })
  @Prop({ type: Number })
  rawCcy?: number

  /** `rawCcy` after markup — the amount actually charged (its magnitude equals this row's `credits`). */
  @Field(() => Float, { nullable: true })
  @Prop({ type: Number })
  billedCcy?: number

  /**
   * Deterministic idempotency key for a caller that may retry the same billable unit of
   * work (e.g. a BullMQ batch chunk: `${batchId}:${chunkIndex}`). A transaction already
   * persisted for this key means the caller already paid for it — `RecordAndDebitAiUsage`
   * returns the existing row instead of double-billing on retry. Absent for call sites with
   * no retry surface (single-shot scans) and for non-Debit rows, hence sparse.
   */
  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  chargeKey?: string

  /**
   * Generic reference to the work this charge paid for. The row's `feature` decides what it
   * points at — a `quickscore.processBatchScan` row references a `ScanBatch._id`, a
   * `quickscore.processScan` row an `AssessmentResult._id`, a `quickscore.scanAnswerKey` row an
   * `Assessment._id`. Deliberately untyped and generic so a new billed flow can reference its own
   * record without another field. Absent on every row written before this field existed and on
   * every call site that supplies none, hence sparse and nullable with no backfill.
   */
  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  refId?: string

  /**
   * `billedCcy - rawCcy` — resolved by `AiCreditTransactionResolver`, never persisted (no
   * `@Prop`, no migration). Nullable because a Grant/Adjustment/Purchase row carries no
   * `rawCcy`/`billedCcy` to subtract, and because a schema that registers
   * `AiUsageResolver` without `AiCreditTransactionResolver` has no resolver behind the field,
   * which must then resolve to null rather than throw a non-null-field violation.
   */
  @Field(() => Float, { nullable: true })
  markupCcy?: number

  // --- Acting-teacher display fields, resolver-only (no `@Prop`) — `createdBy` above is a bare
  // `User._id`; these carry the resolved display name/email for a pooled balance's ledger, so a
  // reader can see WHICH member drew it down without a second round trip per row. Batch-attached
  // by whichever read populates them (`GetMyCreditLedger`, for the mobile Credits screen). ---

  /** Resolved from `createdBy` via the `User` repository. Absent when unresolved (e.g. a deleted user) or not populated by the read. */
  @Field(() => String, { nullable: true })
  createdByName?: string

  /** Resolved from `createdBy` via the `User` repository. Absent when unresolved (e.g. a deleted user) or not populated by the read. */
  @Field(() => String, { nullable: true })
  createdByEmail?: string
}
